import { type Way, at, trailAt } from '../../data/full/path-layout';
import { nextFloat, seedRng } from '../../shared/prng';

const TAU = Math.PI * 2;
const WAKE = {
  gap: 16,
  spread: 120,
  wander: 10,
  asleep: 0.06,
  sigma: 170,
  start: 300,
  pace: 150,
  rush: 280,
  wait: 440,
  catch: 200,
  paceSmoothing: 0.6,
  lightSmoothing: 0.5,
  beyond: 120,
  breath: 0.15,
} as const;

interface WakeFly {
  readonly along: number;
  readonly offset: number;
  readonly size: number;
  readonly waves: readonly (readonly [number, number])[];
  light: number;
}

export interface Wake {
  readonly way: Way;
  readonly heading: 1 | -1;
  readonly flies: readonly WakeFly[];
  zoneAlong: number;
  speed: number;
}

export interface Fly {
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly alpha: number;
}

export function createWake(way: Way, lead: number, heading: 1 | -1, seed: number): Wake {
  const rng = seedRng((seed ^ 0x5bd1e995) >>> 0);
  const count = Math.round(way.length / WAKE.gap);
  const wave = (scale: number): [number, number] => [
    (0.08 + nextFloat(rng) * 0.22) * scale,
    nextFloat(rng) * TAU,
  ];
  const flies = Array.from({ length: count }, (_, i) => ({
    along: (i + nextFloat(rng) * 0.9) * WAKE.gap,
    offset: (nextFloat(rng) - 0.5) * 2 * WAKE.spread,
    size: 1.5 + nextFloat(rng) * 1.1,
    waves: [wave(1), wave(1.7), wave(1), wave(1.6)],
    light: WAKE.asleep,
  }));
  return { way, heading, flies, zoneAlong: lead + heading * WAKE.start, speed: WAKE.pace };
}

export function stepWake(wake: Wake, lead: number, seconds: number): void {
  const { way, heading } = wake;
  const ahead = heading * (wake.zoneAlong - lead);
  const target = ahead > WAKE.wait ? 0 : ahead < WAKE.catch ? WAKE.rush : WAKE.pace;
  wake.speed += (target - wake.speed) * (1 - Math.exp(-seconds / WAKE.paceSmoothing));
  const end = heading > 0 ? way.length + WAKE.beyond : -WAKE.beyond;
  wake.zoneAlong += heading * wake.speed * seconds;
  wake.zoneAlong = heading > 0 ? Math.min(end, wake.zoneAlong) : Math.max(end, wake.zoneAlong);
  const ease = 1 - Math.exp(-seconds / WAKE.lightSmoothing);
  for (const fly of wake.flies) {
    const distance = (fly.along - wake.zoneAlong) / WAKE.sigma;
    const want = WAKE.asleep + (1 - WAKE.asleep) * Math.exp(-0.5 * distance * distance);
    fly.light += (want - fly.light) * ease;
  }
}

export function wakeFlies(wake: Wake, seconds: number, beat: number, calm: boolean): Fly[] {
  const breath = calm ? 0 : -WAKE.breath * Math.cos(TAU * beat);
  return wake.flies.map(({ along, offset, size, waves, light }) => {
    const [x1 = 0, x2 = 0, y1 = 0, y2 = 0] = waves.map(([hertz, phase]) =>
      calm ? 0 : Math.sin(TAU * hertz * seconds + phase),
    );
    const spot = at(
      wake.way,
      along + WAKE.wander * (0.65 * x1 + 0.35 * x2),
      trailAt(wake.way, along) + offset + WAKE.wander * (0.65 * y1 + 0.35 * y2),
    );
    return { ...spot, size, alpha: light * (1 + breath) };
  });
}
